/**
 * CustomStatusPopover — Imposta stato personalizzato (emoji + testo + durata)
 * Notion-style design
 */

import React, { useState } from 'react';
import { X, Clock } from 'lucide-react';
import { usePresenceStore } from '../../lib/stores/presenceStore';

interface CustomStatusPopoverProps {
  onClose: () => void;
}

const PRESETS = [
  { emoji: '🗓️', text: 'In riunione' },
  { emoji: '🏖️', text: 'In ferie' },
  { emoji: '🔴', text: 'Non disturbare' },
  { emoji: '☕', text: 'Torno subito' },
  { emoji: '🏠', text: 'Lavoro da casa' },
  { emoji: '🤒', text: 'Malato' },
];

const DURATIONS = [
  { label: '30 minuti', ms: 30 * 60 * 1000 },
  { label: '1 ora', ms: 60 * 60 * 1000 },
  { label: '4 ore', ms: 4 * 60 * 60 * 1000 },
  { label: 'Oggi', ms: 0 }, // special: until end of day
  { label: 'Nessun limite', ms: -1 },
];

export function CustomStatusPopover({ onClose }: CustomStatusPopoverProps) {
  const { setMyCustomStatus, clearMyCustomStatus } = usePresenceStore();
  const [emoji, setEmoji] = useState('');
  const [text, setText] = useState('');
  const [selectedDuration, setSelectedDuration] = useState(4); // default: nessun limite

  const handleSave = async () => {
    if (!text.trim() && !emoji) { onClose(); return; }
    const dur = DURATIONS[selectedDuration];
    let expiresAt: string | null = null;
    if (dur.ms > 0) {
      expiresAt = new Date(Date.now() + dur.ms).toISOString();
    } else if (dur.ms === 0) {
      // End of day
      const eod = new Date();
      eod.setHours(23, 59, 59, 999);
      expiresAt = eod.toISOString();
    }
    await setMyCustomStatus(emoji, text.trim(), expiresAt);
    onClose();
  };

  const handleClear = async () => {
    await clearMyCustomStatus();
    onClose();
  };

  const handlePreset = (preset: typeof PRESETS[0]) => {
    setEmoji(preset.emoji);
    setText(preset.text);
  };

  return (
    <div
      className="w-full bg-[#FBFBFA] dark:bg-gray-800/50"
      style={{ borderBottom: '1px solid rgba(55,53,47,0.09)' }}
    >
      <div className="px-3 py-2.5 space-y-2.5">
        {/* Input */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={emoji}
            onChange={e => setEmoji(e.target.value.slice(0, 2))}
            placeholder="😊"
            className="w-10 h-9 text-center text-lg rounded-md outline-none"
            style={{ backgroundColor: 'rgba(55,53,47,0.06)', border: '1px solid transparent' }}
            onFocus={e => (e.target.style.borderColor = 'rgba(35,131,226,0.4)')}
            onBlur={e => (e.target.style.borderColor = 'transparent')}
          />
          <input
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Cosa stai facendo?"
            className="flex-1 h-9 px-3 text-[13px] rounded-md outline-none"
            style={{ backgroundColor: 'rgba(55,53,47,0.06)', color: '#37352F', border: '1px solid transparent' }}
            onFocus={e => (e.target.style.borderColor = 'rgba(35,131,226,0.4)')}
            onBlur={e => (e.target.style.borderColor = 'transparent')}
            autoFocus
          />
        </div>

        {/* Presets */}
        <div className="flex flex-wrap gap-1">
          {PRESETS.map(p => (
            <button
              key={p.text}
              onClick={() => handlePreset(p)}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] transition-colors"
              style={{ backgroundColor: 'rgba(55,53,47,0.06)', color: '#37352F' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.12)')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.06)')}
            >
              <span>{p.emoji}</span> {p.text}
            </button>
          ))}
        </div>

        {/* Duration */}
        <div>
          <div className="flex items-center gap-1 mb-1.5">
            <Clock className="w-3 h-3" style={{ color: '#787774' }} strokeWidth={1.5} />
            <span className="text-[11px] font-medium" style={{ color: '#787774' }}>Durata</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {DURATIONS.map((d, i) => (
              <button
                key={d.label}
                onClick={() => setSelectedDuration(i)}
                className="px-2 py-1 rounded-md text-[11px] transition-colors"
                style={{
                  backgroundColor: i === selectedDuration ? 'rgba(35,131,226,0.1)' : 'rgba(55,53,47,0.04)',
                  color: i === selectedDuration ? '#2383E2' : '#787774',
                  fontWeight: i === selectedDuration ? 600 : 400,
                }}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={!text.trim() && !emoji}
            className="px-3 py-1 rounded-md text-[11px] font-medium text-white transition-colors disabled:opacity-40"
            style={{ backgroundColor: '#2383E2' }}
          >
            Salva
          </button>
          <button
            onClick={handleClear}
            className="px-3 py-1 rounded-md text-[11px] transition-colors"
            style={{ color: '#787774' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.06)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            Cancella
          </button>
          <button
            onClick={onClose}
            className="ml-auto px-2 py-1 rounded-md text-[11px] transition-colors"
            style={{ color: '#787774' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.06)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
